import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import { decamelize } from 'humps';
import { fromJS } from 'immutable';
import Fetcher from '../../common/util/Fetcher';
import ExhaustivePaginatedFetcher from '../../common/util/ExhaustivePaginatedFetcher';
import { scrollToItem } from '../../common/actions/scrolling';
import ThunkQueue from '../../common/util/ThunkQueue';
import noop from '../../common/util/noop';
import camelizedVals from '../../common/api/camelizeHelpers';
import { CLONE_QUIZ_ENTRY_CALL, CREATE_QUIZ_ENTRY_CALL, DELETE_QUIZ_ENTRY_CALL, DO_NOTHING, UPDATE_QUIZ_ENTRY_CALL, DELETED_QUIZ_ENTRY, MOVED_QUIZ_ENTRY } from '@instructure/quiz-common';
import { createItem } from './items';
import { createStimulus } from './stimuli';
import { addError } from '../../common/actions/alerts';
import { deleteQuizEntry as deleteQuizEntryAction, deleteStimulusEntry as deleteStimulusEntryAction, moveQuizEntry as moveQuizEntryAction, updateSampleItems as updateSampleItemsAction } from '../../common/actions/quizEntries';
import { set as setUI } from '../../common/actions/ui';
import { associateQuizEntries, disassociateQuizEntry } from '../../common/actions/quizzes';
import { handleQuizEntriesResponse, handleQuizEntryResponse } from '../../common/api/callHandlers';
import { getExistingQuizAndItems } from '../../common/api/quizzes';
import QuizEntry from '../../common/records/QuizEntry';
import t from '@instructure/quiz-i18n/es/format-message';

// Actions that involve an API call should happen in order, to prevent race
// conditions (a user moving a question into a stimulus then immediately
// deleting the stimulus, for example). We use this queue to make sure requests
// wait for previous requests to finish, without preventing UI updates.
var queue = new ThunkQueue(function (error, dispatch, getState) {
  // If an action fails, re-fetch all quiz entries from the backend
  var activeQuizId = getState().getIn(['quizzes', 'activeQuizId']);
  dispatch(getQuizEntries(activeQuizId));
  throw error;
});

// =====================
// =====================
//       GET INDEX
// =====================
// =====================

var handleQuizEntriesGet = function handleQuizEntriesGet(dispatch, quizId, quizEntries) {
  dispatch([handleQuizEntriesResponse(quizEntries, quizId), associateQuizEntries(quizId, quizEntries)]);
  return quizEntries;
};
export function getQuizEntry(quizId, quizEntryId) {
  var url = "/api/quizzes/".concat(quizId, "/quiz_entries/").concat(quizEntryId);
  return function (dispatch, getState) {
    var fetcher = new Fetcher();
    return fetcher.get(url).then(function (data) {
      return handleQuizEntriesGet(dispatch, quizId, [data]);
    });
  };
}
export function getQuizEntries(quizId) {
  var cb = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
  var url = "/api/quizzes/".concat(quizId, "/quiz_entries");
  return function (dispatch, getState) {
    var fetcher = new ExhaustivePaginatedFetcher();
    return fetcher.get(url).then(function (data) {
      return handleQuizEntriesGet(dispatch, quizId, data);
    }).then(cb);
  };
}
export function getSampleItemsForQuizEntry(quizId, quizEntryId) {
  var url = "/api/quizzes/".concat(quizId, "/quiz_entries/").concat(quizEntryId, "/sample_items");
  return function (dispatch, getState) {
    var fetcher = new ExhaustivePaginatedFetcher();
    return fetcher.get(url).then(function (data) {
      var sampleItems = camelizedVals(data);
      dispatch(updateSampleItemsAction(quizEntryId, fromJS(sampleItems)));
    });
  };
}

// =====================
// =====================
//       CREATE
// =====================
// =====================

function createEntryAndQuizEntry(quizId, entry, quizEntry, entryCreationCb, quizEntryCreationCb) {
  return function (dispatch, getState) {
    var entryProperties = quizEntry.getProperties();
    return dispatch(entryCreationCb(entry, quizId, 'quiz')).then(function (entryData) {
      return dispatch(quizEntryCreationCb(quizId, entryData.id, quizEntry.get('entryType'), entryProperties));
    });
  };
}
export var createStimulusAndQuizEntry = queue.wrapActionCreator(function (quizId, stimulus, quizEntry) {
  var stimulusCreationCb = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : createStimulus;
  var quizEntryCreationCb = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : createQuizEntry;
  return createEntryAndQuizEntry(quizId, stimulus, quizEntry, stimulusCreationCb, quizEntryCreationCb);
});
export var createItemAndQuizEntry = queue.wrapActionCreator(function (quizId, item, quizEntry) {
  var itemCreationCb = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : createItem;
  var quizEntryCreationCb = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : createQuizEntry;
  return createEntryAndQuizEntry(quizId, item, quizEntry, itemCreationCb, quizEntryCreationCb);
});
export function createQuizEntry(quizId, entryId, entryType, entryProperties) {
  var requireFocus = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : true;
  var url = "/api/quizzes/".concat(quizId, "/quiz_entries");
  var quizEntryParams = _objectSpread({
    quizId: quizId,
    entryType: entryType,
    entryId: entryId
  }, entryProperties);
  var body = JSON.stringify({
    quiz_entry: quizEntryParams
  });
  return function (dispatch, getState) {
    var fetcher = new Fetcher({
      callType: CREATE_QUIZ_ENTRY_CALL,
      onError: function onError() {
        return addError(t('Failed to create quiz entry.'));
      }
    });
    // TODO: this re-fetches all quiz entries after the new quiz entry is
    // created. This causes the new question to disappear and reappear after a
    // noticeable delay. Should we add the quiz entry optimistically, for
    // better UX?
    return fetcher.post(url, {
      body: body
    }).then(function (response) {
      var scrollCallback = function scrollCallback() {
        return dispatch(scrollToItem(response.id, requireFocus));
      };
      return dispatch(getQuizEntries(quizId, scrollCallback));
    });
  };
}

// =====================
// =====================
//    UPDATE, CLONE & MOVE
// =====================
// =====================

// UPDATE HELPER
function updateBody(updatedProperties) {
  var properties = updatedProperties.properties;
  if (properties && properties.selection === 'all') {
    properties.sampleNum = null;
  }
  var validProperties = Object.keys(updatedProperties).map(decamelize).concat(['properties']);
  var entryProperties = pick({
    points_possible: parseFloat(updatedProperties.pointsPossible),
    position: updatedProperties.position,
    stimulus_quiz_entry_id: updatedProperties.stimulusQuizEntryId,
    entry_id: updatedProperties.entryId,
    entry_type: updatedProperties.entryType,
    properties: properties
  }, validProperties);
  var isDefined = function isDefined(val) {
    return val !== null && val !== void 0;
  };
  var entryPropsToUpdate = pickBy(entryProperties, isDefined);
  return JSON.stringify({
    quiz_entry: entryPropsToUpdate
  });
}

// Update the given quiz entry. If handleResponse is true, the redux store will
// be updated with response data when it comes back. You may want to set
// handleResponse to false for some optimistic update flows.
export function updateQuizEntry(quizId, quizEntryId, updatedProperties) {
  var _ref = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {},
    _ref$handleResponse = _ref.handleResponse,
    handleResponse = _ref$handleResponse === void 0 ? true : _ref$handleResponse;
  var url = "/api/quizzes/".concat(quizId, "/quiz_entries/").concat(quizEntryId);
  var fetcher = new Fetcher({
    callType: UPDATE_QUIZ_ENTRY_CALL,
    onError: function onError() {
      return addError(t('Failed to update quiz entry.'));
    }
  });
  var body = updateBody(updatedProperties);
  return /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(dispatch, getState) {
      var response;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return fetcher.patch(url, {
              body: body
            });
          case 2:
            response = _context.sent;
            if (handleResponse) {
              dispatch(handleQuizEntryResponse(response));
            }
          case 4:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x, _x2) {
      return _ref2.apply(this, arguments);
    };
  }();
}
export function cloneQuizEntry(quizId, quizEntryId, updatedProperties) {
  var url = "/api/quizzes/".concat(quizId, "/quiz_entries/").concat(quizEntryId, "/clone");
  var fetcher = new Fetcher({
    callType: CLONE_QUIZ_ENTRY_CALL,
    onError: function onError() {
      return addError(t('Failed to clone quiz entry.'));
    }
  });
  var body = updateBody(updatedProperties);
  return function (dispatch, getState) {
    return fetcher.post(url, {
      body: body
    }).then(function (qeData) {
      dispatch(handleQuizEntryResponse(qeData));
      return qeData;
    }).then(function () {
      return dispatch(getExistingQuizAndItems(quizId));
    });
  };
}
export function moveQuizEntry(quizId, position, stimulusQuizEntryId, quizEntryId, moveTempItem) {
  var updateAction = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : updateQuizEntry;
  return function (dispatch, getState) {
    // Optimistically move the quiz entry in the redux store without waiting
    // for a response.
    return dispatch([moveQuizEntryAction(quizEntryId, position, stimulusQuizEntryId), setUI(MOVED_QUIZ_ENTRY, quizEntryId)]).then(function () {
      moveTempItem && moveTempItem(); // this functionality was temporarily disabled in 9c03aa9
      var queuedUpdate = queue.wrapThunk(updateAction(quizId, quizEntryId, {
        position: position,
        stimulusQuizEntryId: stimulusQuizEntryId
      }, {
        handleResponse: false
      }));
      return dispatch(queuedUpdate);
    });
  };
}
export function moveImmutableQuizEntry(quizId, position, stimulusQuizEntryId, quizEntryId) {
  var createItemAction = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : createItem;
  var updateAction = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : updateQuizEntry;
  return function (dispatch, getState) {
    return dispatch(moveQuizEntry(quizId, position, stimulusQuizEntryId, quizEntryId, void 0,
    // We pass a noop action creator here to only move the quiz entry in our
    // local state, not on the backend. We need to wait to move the entry on
    // the backend until after the new item has been created, otherwise the
    // backend will complain about trying to update an immutable item.
    function () {
      return {
        type: DO_NOTHING
      };
    })).then(function () {
      return dispatch(createItemAndMoveQuizEntry(quizId, position, stimulusQuizEntryId, quizEntryId, createItemAction, updateAction));
    });
  };
}
var createItemAndMoveQuizEntry = queue.wrapActionCreator(function (quizId, position, stimulusQuizEntryId, quizEntryId) {
  var createItemAction = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : createItem;
  var updateAction = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : updateQuizEntry;
  return function (dispatch, getState) {
    var quizEntry = QuizEntry.create(getState().getIn(['quizEntries', quizEntryId]));
    var workingItem = quizEntry.getEntry().getWorkingInstance();
    return dispatch(createItemAction(workingItem, quizId, 'quiz', workingItem.id)).then(function (_ref3) {
      var id = _ref3.id;
      return dispatch(updateAction(quizId, quizEntryId, {
        entryId: id,
        position: position,
        stimulusQuizEntryId: stimulusQuizEntryId
      }, {
        handleResponse: false
      }));
    });
  };
});

// =====================
// =====================
//       DESTROY
// =====================
// =====================

function quizEntryDeleted(dispatch, quizEntry) {
  setTimeout(function () {
    dispatch(setUI(DELETED_QUIZ_ENTRY, {
      position: quizEntry.position,
      inStimulus: quizEntry.isInStimulus
    }));
  }, 100);
}
var deleteQuizEntry = queue.wrapActionCreator(function (url, params) {
  return function (dispatch, getState) {
    var fetcher = new Fetcher({
      callType: DELETE_QUIZ_ENTRY_CALL,
      onError: function onError() {
        return addError(t('Failed to remove quiz entry.'));
      }
    });
    return fetcher["delete"](url, params);
  };
});
export function removeQuizEntry(quizId, quizEntry) {
  var url = "/api/quizzes/".concat(quizId, "/quiz_entries/").concat(quizEntry.id);
  return function (dispatch, getState) {
    return dispatch([deleteQuizEntryAction(quizEntry.id), disassociateQuizEntry(quizId, quizEntry.id)]).then(function () {
      quizEntryDeleted(dispatch, quizEntry);
      return dispatch(deleteQuizEntry(url));
    });
  };
}
export function removeStimulusQuizEntry(quizId, stimulusEntry, childEntries) {
  var url = "/api/quizzes/".concat(quizId, "/quiz_entries/").concat(stimulusEntry.id, "/stimuli");
  var childEntryIds = childEntries.map(function (e) {
    return e.id;
  });
  return function (dispatch, getState) {
    var body = JSON.stringify({
      child_ids: childEntryIds
    });
    return dispatch([deleteStimulusEntryAction(stimulusEntry.id, childEntryIds), disassociateQuizEntry(quizId, stimulusEntry.id)].concat(_toConsumableArray(childEntryIds.map(function (id) {
      return disassociateQuizEntry(quizId, id);
    })))).then(function () {
      quizEntryDeleted(dispatch, stimulusEntry);
      return dispatch(deleteQuizEntry(url, {
        body: body
      }));
    });
  };
}